import { AnyID, Getter } from '@types'
import {
  isRelaxIntegratedUnitByDefaults,
  parseRelaxArmVariantCatalog,
} from '@utils/atemporal/relaxArmVariants'
import {
  isCornerXLBrazoCatalog,
  isModularCornerSnapCatalog,
  isOreja,
  isPouff,
  isRinconeroCatalog,
  isRinconeroDerecha,
  isRinconeroIzquierda,
  defaultThicknessForDrawCatalog,
  defaultWidthForDrawCatalog,
  isStraightModularSnapCatalog,
  modulePlanWidth,
  resolveStraightModuleRinconeroSnap,
  rinconeroHandednessFromItem,
  rinconeroPlanWidth,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { allowedArmSideForUnit } from '@utils/linkedArms/singleArmSeriesRules'
import { getArmSideSign, resolveLayoutWidth } from '@utils/linkedArms/armPlacement'
import {
  resolveModuleSideOccupantId,
  stripArmIdsFromSnappedSides,
} from '@utils/linkedArms/armOccupancy'
import { resolveValidPouffInsertPose } from '@utils/sceneObjects/items/pouffFreeMotion'
import { Quaternion, Vector3 } from 'three'
import { drawSnapLog } from './drawSnapLog'
import type { DrawItemOptions } from './itemActions'

/**
 * Contexto necesario para calcular la colocación inicial de un item por
 * auto-snap a una unidad existente (padre). Reúne el estado que `drawItem`
 * ya tiene calculado en el momento de decidir dónde colocar el nuevo item.
 */
export type ParentSnapPlacementContext = {
  store: ReturnType<Getter>
  item: any
  catalogID: string
  options?: DrawItemOptions
  sourceCurrentObjectID: AnyID | ''
  globalLastSelectedId: string
  globalLastSelectedAgeMs: number
  recentGlobalLastSelectedId: string
  logDrawParentSnap: (step: string, data?: Record<string, unknown>) => void
  isCushionLikeSceneObject: (obj: any) => boolean
  isArmSceneObject: (obj: any) => boolean
}

/**
 * Resultado del cálculo de colocación. `placed` devuelve la transformación
 * final (posición/rotación) y, si procede, el snap pendiente al padre.
 * `abort` indica que la colocación no es válida (lado ocupado, sin candidatos,
 * etc.); el caller debe abortar el dibujo con esa razón.
 */
export type ParentSnapPlacementResult =
  | {
      status: 'placed'
      centerPosition: Vector3
      quaternion: Quaternion
      pendingParentSnap: { anchorId: AnyID; snapSide: 'left' | 'right' } | null
    }
  | { status: 'abort'; reason: string; data?: Record<string, unknown> }

const SIDE_TOLERANCE_MM = 80
const DEPTH_ALIGNMENT_TOLERANCE_MM = 120

const isPouffSceneItem = (sceneItem: any) =>
  Boolean(sceneItem && isPouff(String(sceneItem.catalogID ?? '')))

const resolveSceneItem = (store: ReturnType<Getter>, sceneItems: any[], id: string | undefined) => {
  if (!id) return null
  return (
    (store.items.get(id as AnyID) as any) ??
    sceneItems.find((candidate) => String(candidate?.id) === id) ??
    null
  )
}

/** ¿Hay un puff ya colocado en el lateral exterior de este ancla? (bloquea dibujar BR ahí). */
const pouffAdjacentToAnchorSide = (
  anchor: any,
  side: 'left' | 'right',
  sceneItems: any[],
  excludeItemId: string,
) => {
  if (!anchor?.matrix) return false

  const anchorPos = new Vector3()
  const anchorQuat = new Quaternion()
  anchor.matrix.decompose(anchorPos, anchorQuat, new Vector3())
  const anchorX = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)
  const anchorZ = new Vector3(0, 0, 1).applyQuaternion(anchorQuat)
  const anchorWidth = Number(anchor.width || 0)

  return sceneItems.some((candidate) => {
    if (!isPouffSceneItem(candidate)) return false
    if (String(candidate.id) === excludeItemId) return false
    if (!candidate.matrix) return false

    const pouffPos = new Vector3()
    candidate.matrix.decompose(pouffPos, new Quaternion(), new Vector3())
    const delta = pouffPos.clone().sub(anchorPos)
    const sideProjection = delta.dot(anchorX)
    const depthProjection = Math.abs(delta.dot(anchorZ))
    if (depthProjection > DEPTH_ALIGNMENT_TOLERANCE_MM) return false

    const pouffWidth = Number(candidate.width || 0)
    if (side === 'right') {
      return Math.abs(sideProjection - anchorWidth) <= SIDE_TOLERANCE_MM + pouffWidth
    }
    return Math.abs(sideProjection + pouffWidth) <= SIDE_TOLERANCE_MM
  })
}

/** Brazo BR* anclado a este módulo en el labio indicado, si existe. */
const getArmOnUnitSide = (
  unit: any,
  side: 'left' | 'right',
  sceneItems: any[],
  excludeItemId?: string,
) => {
  if (!unit?.matrix) return null
  const sideSign: 1 | -1 = side === 'left' ? -1 : 1
  return (
    sceneItems.find((candidate) => {
      if (!candidate || String(candidate.id) === String(excludeItemId || '')) return false
      if (!/^BR\d+(-\d+)?$/i.test(String(candidate.catalogID || ''))) return false
      if (String(candidate.firstParentID || '') !== String(unit.id)) return false
      if (!candidate.matrix) return false
      return getArmSideSign(unit, candidate) === sideSign
    }) ?? null
  )
}

const hasArmOnUnitSide = (
  unit: any,
  side: 'left' | 'right',
  sceneItems: any[],
  excludeItemId?: string,
) => Boolean(getArmOnUnitSide(unit, side, sceneItems, excludeItemId))

/**
 * Colocación a tope en la cara exterior de un brazo (pouff u unidad que no hospeda BR).
 * No escribe snappedSides del módulo: el brazo permanece en su extremo.
 */
const computePlacementBeyondArm = (
  arm: any,
  item: any,
  store: ReturnType<Getter>,
): { position: Vector3; quaternion: Quaternion } | null => {
  if (!arm?.matrix) return null

  const armPos = new Vector3()
  const armQuat = new Quaternion()
  arm.matrix.decompose(armPos, armQuat, new Vector3())
  const armXAxis = new Vector3(1, 0, 0).applyQuaternion(armQuat)

  const parentId = arm.firstParentID
  const parent = parentId ? (store.items.get(parentId as AnyID) as any) : null
  let exteriorSign: 1 | -1 = 1
  if (parent?.matrix) {
    const parentPos = new Vector3()
    const parentQuat = new Quaternion()
    parent.matrix.decompose(parentPos, parentQuat, new Vector3())
    const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)
    exteriorSign = armPos.clone().sub(parentPos).dot(parentXAxis) >= 0 ? 1 : -1
  }

  const armWidth = resolveLayoutWidth(arm) || Number(arm.width || 0)
  const itemWidth = Number(item.width || 0)
  const widthToUse = exteriorSign > 0 ? armWidth : itemWidth
  const position = armPos.clone().add(armXAxis.clone().multiplyScalar(exteriorSign * widthToUse))
  position.y = Number(item.y ?? 0)

  return { position, quaternion: armQuat.clone() }
}

/**
 * Calcula la posición y rotación con la que se coloca un item nuevo cuando se
 * ancla automáticamente a una unidad existente (snap lateral al padre).
 *
 * Contiene toda la lógica de selección de ancla y lado (incluyendo casos de
 * rinconeros/orejas y la preferencia por el ancla seleccionada en UI). No
 * muta la escena: solo decide la transformación final del item.
 */
export const computeParentSnapPlacement = (
  ctx: ParentSnapPlacementContext,
): ParentSnapPlacementResult => {
  const {
    store,
    item,
    catalogID,
    options,
    sourceCurrentObjectID,
    globalLastSelectedId,
    globalLastSelectedAgeMs,
    recentGlobalLastSelectedId,
    logDrawParentSnap,
    isCushionLikeSceneObject,
    isArmSceneObject,
  } = ctx

  const normalizedDrawingCatalog = String(catalogID || '')
    .trim()
    .toLowerCase()
  drawSnapLog('inicio', {
    drawingCatalog: normalizedDrawingCatalog,
    itemId: String(item?.id ?? ''),
    preDrawAnchorId: String(options?.preDrawSelectedAnchorId || sourceCurrentObjectID || ''),
    useChainedDrawMatrix: Boolean(options?.useChainedDrawMatrix),
  })

  const centerPosition = new Vector3()
  const quaternion = new Quaternion()
  let pendingParentSnap: {
    anchorId: AnyID
    snapSide: 'left' | 'right'
  } | null = null

  const toIncrement = (sceneItem: any) => parseInt(String(sceneItem?.increment || ''), 10)
  const hasValidIncrement = (sceneItem: any) => !isNaN(toIncrement(sceneItem))
  const canUseAsParentAnchor = (sceneItem: any) =>
    Boolean(
      sceneItem &&
      (sceneItem.type === 'items' || sceneItem.type === 'linkedItem') &&
      sceneItem.id !== item.id &&
      hasValidIncrement(sceneItem) &&
      sceneItem.visible !== false &&
      (sceneItem as any).canCollide !== false &&
      !isCushionLikeSceneObject(sceneItem) &&
      !isArmSceneObject(sceneItem),
    )

  const drawingIsArm = /^br\d+(-\d+)?$/i.test(normalizedDrawingCatalog)
  const drawingIsPouff = isPouff(normalizedDrawingCatalog)

  const drawingIsOreja = isOreja(normalizedDrawingCatalog)

  // Los brazos solo se anclan a unidades normales: nunca a rinconeros (166-/181-185),
  // pouffs (186-188) ni módulos RELAX integrados (167/172), ni en su catálogo base ni
  // con variante de brazo (ej. «167 BR1 D»). Las RELAX gestionan sus brazos mediante
  // cambio de GLB, así que un BR independiente jamás debe anclarse a ellas.
  // EXCEPCIÓN: las orejas (177-180) y las Corner XL BRAZO (181-185), terminadas en
  // D/I, SÍ admiten un brazo, en su lado «con mano». Deben poder usarse como ancla
  // para que el brazo colisione a su lado y no caiga en la rama «sin anclas» (que lo
  // colocaría encima, en delta 0).
  const isNormalAnchorForArm = (sceneItem: any) => {
    const anchorCatalog = String(sceneItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    const isCornerArmUnit = isCornerXLBrazoCatalog(anchorCatalog)
    return (
      (isCornerArmUnit || !isModularCornerSnapCatalog(anchorCatalog)) &&
      !isPouff(anchorCatalog) &&
      parseRelaxArmVariantCatalog(anchorCatalog) == null &&
      !isRelaxIntegratedUnitByDefaults(anchorCatalog)
    )
  }

  const regularItems = Array.from(store.items.values()).filter((sceneItem) =>
    canUseAsParentAnchor(sceneItem),
  )
  const eligibleRegularItems = drawingIsArm
    ? regularItems.filter((sceneItem: any) => isNormalAnchorForArm(sceneItem))
    : regularItems
  const sceneItems = Array.from(store.items.values()) as any[]
  const drawingIsOrejaIzquierda = drawingIsOreja && normalizedDrawingCatalog.endsWith('i')
  const drawingIsOrejaDerecha = drawingIsOreja && normalizedDrawingCatalog.endsWith('d')
  const drawingIsModularCorner = isModularCornerSnapCatalog(normalizedDrawingCatalog)
  const drawingHand = rinconeroHandednessFromItem({ catalogID: normalizedDrawingCatalog })
  const drawingIsRinconeroIzquierda =
    drawingIsModularCorner &&
    !drawingIsOreja &&
    isRinconeroIzquierda(normalizedDrawingCatalog, drawingHand)
  const drawingIsRinconeroDerecha =
    drawingIsModularCorner &&
    !drawingIsOreja &&
    isRinconeroDerecha(normalizedDrawingCatalog, drawingHand)
  const drawingWantsLeftSide = drawingIsOrejaIzquierda || drawingIsRinconeroIzquierda
  const drawingWantsRightSide = drawingIsOrejaDerecha || drawingIsRinconeroDerecha
  const desiredSnapSide: 'left' | 'right' | null = drawingWantsLeftSide
    ? 'left'
    : drawingWantsRightSide
      ? 'right'
      : null
  const enforceCatalogSide = drawingIsOreja && desiredSnapSide != null

  const isSideCompatibleAnchor = (sceneItem: any, side: 'left' | 'right') => {
    const anchorCatalog = String(sceneItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    // Brazo sobre unidad «con mano» (D/I, incluidas orejas 177–180): el BR solo
    // puede ir en el labio EXTERIOR de su mano (i→izq, d→der). Tiene prioridad
    // sobre la regla de adyacencia de módulos de las orejas (labio interior).
    if (drawingIsArm) {
      const handedArmSide = allowedArmSideForUnit(anchorCatalog)
      if (handedArmSide != null) {
        return side === (handedArmSide === 1 ? 'right' : 'left')
      }
    }
    // Oreja como ancla de módulo (no brazo): solo el labio interior hacia la fila.
    if (isOreja(anchorCatalog) && !drawingIsArm) {
      const allowedSide: 'left' | 'right' | null = anchorCatalog.endsWith('d')
        ? 'left'
        : anchorCatalog.endsWith('i')
          ? 'right'
          : null
      return allowedSide == null || allowedSide === side
    }

    if (isRinconeroCatalog(anchorCatalog)) {
      // Oreja (177–180) sobre rinconero 166-: lado según mano de la unidad que se dibuja.
      if (drawingIsOreja) {
        if (drawingIsOrejaIzquierda) return side === 'left'
        if (drawingIsOrejaDerecha) return side === 'right'
      }
      // Módulos rectos (160, 167, …): ambas caras exteriores del rinconero son válidas.
      const drawingIsStraightModule =
        isStraightModularSnapCatalog(normalizedDrawingCatalog) && !drawingIsArm && !drawingIsPouff
      if (drawingIsStraightModule) return true

      const anchorIsOreja = isOreja(anchorCatalog)
      const anchorHand = rinconeroHandednessFromItem(sceneItem)
      const anchorIsLeft =
        !anchorIsOreja && isRinconeroIzquierda(anchorCatalog, anchorHand)
      const allowedSide: 'left' | 'right' = anchorIsLeft ? 'left' : 'right'
      return allowedSide === side
    }

    return true
  }

  const isRinconeroPairCompatible = (sceneItem: any) => {
    const anchorCatalog = String(sceneItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    if (!isRinconeroCatalog(anchorCatalog)) return true
    const anchorIsOreja = isOreja(anchorCatalog)
    const anchorHand = rinconeroHandednessFromItem(sceneItem)
    const anchorIsLeft =
      !anchorIsOreja && isRinconeroIzquierda(anchorCatalog, anchorHand)
    return anchorIsLeft === drawingIsRinconeroIzquierda
  }

  const findAnchorBySide = (side: 'left' | 'right') => {
    let freeSideCandidates = eligibleRegularItems.filter((sceneItem: any) => {
      const resolveItem = (id: string) => resolveSceneItem(store, sceneItems, id)
      const snappedSides = stripArmIdsFromSnappedSides(
        (sceneItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {},
        resolveItem,
      )
      // Un brazo en el labio no bloquea colocar un módulo (se reancla al nuevo extremo).
      if (drawingIsArm) {
        return (
          !resolveModuleSideOccupantId(snappedSides[side], resolveItem) &&
          !hasArmOnUnitSide(sceneItem, side, sceneItems, String(item.id))
        )
      }
      return !resolveModuleSideOccupantId(snappedSides[side], resolveItem)
    })

    if (drawingIsArm) {
      freeSideCandidates = freeSideCandidates.filter(
        (sceneItem) =>
          !pouffAdjacentToAnchorSide(sceneItem, side, sceneItems, String(item.id)),
      )
    }

    const compatible = freeSideCandidates.filter(
      (sceneItem: any) =>
        isSideCompatibleAnchor(sceneItem, side) && isRinconeroPairCompatible(sceneItem),
    )
    return compatible.length > 0 ? compatible : null
  }

  if (eligibleRegularItems.length === 0) {
    // La matriz inicial ya viene de newGetDataFromGLB (x=1000, rotación frontal).
    // No sumar +1000 otra vez: provocaba un salto visible en la primera unidad.
    if (item.matrix) {
      item.matrix.decompose(centerPosition, quaternion, new Vector3())
    } else {
      centerPosition.set(1000, 0, 0)
      quaternion.setFromAxisAngle(new Vector3(0, 1, 0), Math.PI)
    }
  } else {
    const getLastByIncrement = (arr: any[]) =>
      arr.reduce((prev, curr) => {
        const prevIncrement = parseInt(prev.increment, 10)
        const currIncrement = parseInt(curr.increment, 10)
        return currIncrement > prevIncrement ? curr : prev
      })

    const rightCandidates = findAnchorBySide('right') || []
    const leftCandidates = findAnchorBySide('left') || []
    // Nota: findAnchorBySide ya filtra por lado libre (snappedSides) y por
    // compatibilidad de lado (isSideCompatibleAnchor), así que si una oreja
    // aparece aquí es porque su único lado habilitado sigue libre: debe poder
    // usarse como ancla normalmente, igual que cualquier otro candidato.
    const preferredDefaultSide: 'left' | 'right' = rightCandidates.length > 0 ? 'right' : 'left'
    const searchSide: 'left' | 'right' = desiredSnapSide ?? preferredDefaultSide
    const anchorCandidates: any[] = searchSide === 'right' ? rightCandidates : leftCandidates

    logDrawParentSnap('anchor-candidates', {
      drawingCatalog: normalizedDrawingCatalog,
      drawingIsOreja,
      drawingIsRinconeroIzquierda,
      desiredSnapSide,
      searchSide,
      regularItemsCount: eligibleRegularItems.length,
      anchorCandidatesCount: anchorCandidates.length,
      selectedAnchorId: null,
      drawingIsArm,
    })

    if (enforceCatalogSide && anchorCandidates.length === 0) {
      return {
        status: 'abort',
        reason: 'catalog-no-anchor-candidate-on-required-side',
        data: {
          desiredSnapSide,
          drawingCatalog: normalizedDrawingCatalog,
          drawingIsOreja,
        },
      }
    }

    const compatibleSelectedAnchor = false
    const selectedFromLayer = Array.from(store.layers[store.currentLayer].selected.values())[0] as
      | AnyID
      | undefined
    const preferredSelectedAnchorId =
      (sourceCurrentObjectID as AnyID | null | undefined) || selectedFromLayer
    logDrawParentSnap('selected-anchor-context', {
      optionsPreDrawSelectedAnchorId: String(options?.preDrawSelectedAnchorId || ''),
      sourceCurrentObjectID: String(sourceCurrentObjectID || ''),
      selectedFromLayer: String(selectedFromLayer || ''),
      preferredSelectedAnchorId: String(preferredSelectedAnchorId || ''),
      currentObjectIDInStore: String(store.currentObjectID || ''),
      selectedCountInLayer: store.layers[store.currentLayer].selected.size,
      globalLastSelectedId,
      globalLastSelectedAgeMs: Number.isFinite(globalLastSelectedAgeMs)
        ? globalLastSelectedAgeMs
        : null,
      recentGlobalLastSelectedId,
    })
    const selectedAnchorFromStore = preferredSelectedAnchorId
      ? (store.items.get(preferredSelectedAnchorId as AnyID) as any)
      : null
    const selectedAnchorCandidateRaw =
      selectedAnchorFromStore ||
      (preferredSelectedAnchorId
        ? eligibleRegularItems.find(
            (sceneItem: any) => String(sceneItem?.id) === String(preferredSelectedAnchorId),
          )
        : null)
    const selectedAnchorCandidate =
      selectedAnchorCandidateRaw &&
      (!drawingIsArm || isNormalAnchorForArm(selectedAnchorCandidateRaw))
        ? selectedAnchorCandidateRaw
        : null
    const selectedAnchorIsValid =
      Boolean(selectedAnchorCandidate) && canUseAsParentAnchor(selectedAnchorCandidate)
    const resolveOccupantItem = (id: string) => resolveSceneItem(store, sceneItems, id)
    const selectedSnappedSides = stripArmIdsFromSnappedSides(
      ((selectedAnchorCandidate as any)?.snappedSides as
        | Partial<Record<'left' | 'right', string>>
        | undefined) || {},
      resolveOccupantItem,
    )
    const selectedAnchorId = String((selectedAnchorCandidate as any)?.id ?? '')
    const selectedSceneOccupiedSides: Partial<Record<'left' | 'right', string>> = {}
    if (selectedAnchorCandidate?.matrix) {
      const selectedPos = new Vector3()
      const selectedQuat = new Quaternion()
      selectedAnchorCandidate.matrix.decompose(selectedPos, selectedQuat, new Vector3())
      const selectedXAxis = new Vector3(1, 0, 0).applyQuaternion(selectedQuat)
      const selectedZAxis = new Vector3(0, 0, 1).applyQuaternion(selectedQuat)
      const selectedWidth = Number((selectedAnchorCandidate as any)?.width || 0)
      const SIDE_TOLERANCE_MM = 80
      const DEPTH_ALIGNMENT_TOLERANCE_MM = 120
      const HEIGHT_TOLERANCE_MM = 120

      sceneItems.forEach((candidate: any) => {
        if (!canUseAsParentAnchor(candidate)) return
        if (String(candidate?.id) === selectedAnchorId || String(candidate?.id) === String(item.id))
          return
        if (!candidate?.matrix) return

        const candidatePos = new Vector3()
        candidate.matrix.decompose(candidatePos, new Quaternion(), new Vector3())

        const delta = new Vector3(
          candidatePos.x - selectedPos.x,
          candidatePos.y - selectedPos.y,
          candidatePos.z - selectedPos.z,
        )
        const sideProjection = selectedXAxis.dot(new Vector3(delta.x, 0, delta.z))
        const depthProjection = Math.abs(selectedZAxis.dot(new Vector3(delta.x, 0, delta.z)))
        const heightDelta = Math.abs(delta.y)

        if (depthProjection > DEPTH_ALIGNMENT_TOLERANCE_MM || heightDelta > HEIGHT_TOLERANCE_MM) {
          return
        }

        const expectedRightProjection = selectedWidth
        const expectedLeftProjection = -(candidate.width || 0)

        if (Math.abs(sideProjection - expectedRightProjection) <= SIDE_TOLERANCE_MM) {
          selectedSceneOccupiedSides.right = String(candidate.id)
        }
        if (Math.abs(sideProjection - expectedLeftProjection) <= SIDE_TOLERANCE_MM) {
          selectedSceneOccupiedSides.left = String(candidate.id)
        }
      })
    }
    const selectedRightModuleOccupant = resolveModuleSideOccupantId(
      selectedSnappedSides.right,
      resolveOccupantItem,
      String(item.id),
    )
    const selectedLeftModuleOccupant = resolveModuleSideOccupantId(
      selectedSnappedSides.left,
      resolveOccupantItem,
      String(item.id),
    )
    const selectedRightOccupied = Boolean(
      selectedRightModuleOccupant ||
      selectedSceneOccupiedSides.right ||
      sceneItems.some((sceneItem: any) => {
        if (String(sceneItem?.id) === selectedAnchorId) return false
        if (isArmSceneObject(sceneItem)) return false
        const sceneItemSnappedSides = stripArmIdsFromSnappedSides(
          (sceneItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {},
          resolveOccupantItem,
        )
        return String(sceneItemSnappedSides.left || '') === selectedAnchorId
      }),
    )
    const selectedLeftOccupied = Boolean(
      selectedLeftModuleOccupant ||
      selectedSceneOccupiedSides.left ||
      sceneItems.some((sceneItem: any) => {
        if (String(sceneItem?.id) === selectedAnchorId) return false
        if (isArmSceneObject(sceneItem)) return false
        const sceneItemSnappedSides = stripArmIdsFromSnappedSides(
          (sceneItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {},
          resolveOccupantItem,
        )
        return String(sceneItemSnappedSides.right || '') === selectedAnchorId
      }),
    )
    const selectedLeftFree = !selectedLeftOccupied
    const selectedRightOccupiedBySnap = Boolean(selectedRightModuleOccupant)
    const selectedLeftOccupiedBySnap = Boolean(selectedLeftModuleOccupant)
    const shouldForceSelectedLeftPlacement =
      !drawingIsOreja &&
      desiredSnapSide == null &&
      Boolean(selectedAnchorCandidate) &&
      selectedRightOccupiedBySnap &&
      !selectedLeftOccupiedBySnap
    logDrawParentSnap('selected-left-force-check', {
      preferredSelectedAnchorId: String(preferredSelectedAnchorId || ''),
      selectedAnchorId,
      selectedRightOccupiedBySnap,
      selectedLeftOccupiedBySnap,
      shouldForceSelectedLeftPlacement,
    })
    const shouldPreferSelectedLeftAnchor =
      shouldForceSelectedLeftPlacement ||
      (!drawingIsOreja &&
        desiredSnapSide == null &&
        selectedAnchorIsValid &&
        selectedRightOccupied &&
        selectedLeftFree &&
        isSideCompatibleAnchor(selectedAnchorCandidate, 'left') &&
        isRinconeroPairCompatible(selectedAnchorCandidate))

    const fallbackAnchor =
      anchorCandidates.length > 0
        ? getLastByIncrement(anchorCandidates as any[])
        : getLastByIncrement(eligibleRegularItems as any[])

    // Brazo sobre la unidad seleccionada «con mano» (D/I, incluidas orejas): el
    // ancla debe ser esa unidad para que el brazo colisione a su lado de mano.
    const selectedHandedArmSide =
      drawingIsArm && selectedAnchorCandidate
        ? allowedArmSideForUnit((selectedAnchorCandidate as any)?.catalogID)
        : null

    const anchorItem =
      (selectedHandedArmSide != null && (selectedAnchorCandidate as any)) ||
      (shouldPreferSelectedLeftAnchor && (selectedAnchorCandidate as any)) ||
      (compatibleSelectedAnchor as any) ||
      fallbackAnchor
    logDrawParentSnap('selected-left-force-result', {
      shouldPreferSelectedLeftAnchor,
      shouldForceSelectedLeftPlacement,
      anchorId: String((anchorItem as any)?.id ?? ''),
    })

    const anchorPos = new Vector3()
    const anchorQuat = new Quaternion()
    anchorItem.matrix.decompose(anchorPos, anchorQuat, new Vector3())
    const anchorXAxis = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)

    // Brazo sobre unidad «con mano» (D/I, incluidas orejas): se fuerza su único
    // lado permitido. Si ese lado ya está ocupado, abortar: la unidad solo admite
    // un brazo; el llamante reemplaza el brazo existente en vez de apilar otro.
    const anchorHandedArmSide = drawingIsArm
      ? allowedArmSideForUnit((anchorItem as any)?.catalogID)
      : null
    if (anchorHandedArmSide != null) {
      const handedSide: 'left' | 'right' = anchorHandedArmSide === 1 ? 'right' : 'left'
      const resolveItem = (id: string) => resolveSceneItem(store, sceneItems, id)
      const moduleOccupant = resolveModuleSideOccupantId(
        ((anchorItem as any)?.snappedSides as Partial<Record<'left' | 'right', string>>)?.[
          handedSide
        ],
        resolveItem,
        String(item.id),
      )
      const armOccupant = hasArmOnUnitSide(
        anchorItem,
        handedSide,
        sceneItems,
        String(item.id),
      )
      if (moduleOccupant || armOccupant) {
        return {
          status: 'abort',
          reason: 'no-free-side-on-anchor',
          data: {
            itemId: String(item.id),
            anchorId: String((anchorItem as any)?.id ?? ''),
            handedSide,
            handedOccupant: moduleOccupant || 'arm',
          },
        }
      }
    }

    let snapSide: 'left' | 'right' =
      anchorHandedArmSide != null
        ? anchorHandedArmSide === 1
          ? 'right'
          : 'left'
        : shouldPreferSelectedLeftAnchor
          ? 'left'
          : (desiredSnapSide ?? searchSide)
    logDrawParentSnap('selected-left-force-snap-side', {
      snapSide,
      desiredSnapSide,
      searchSide,
      shouldPreferSelectedLeftAnchor,
      shouldForceSelectedLeftPlacement,
    })
    const directionMultiplier = snapSide === 'left' ? -1 : 1

    const anchorSnappedSides = stripArmIdsFromSnappedSides(
      (anchorItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {},
      resolveOccupantItem,
    )
    const usingSelectedAnchor =
      String((anchorItem as any)?.id ?? '') === selectedAnchorId && Boolean(selectedAnchorId)
    let occupiedLeft = resolveModuleSideOccupantId(
      anchorSnappedSides.left,
      resolveOccupantItem,
      String(item.id),
    )
    let occupiedRight = resolveModuleSideOccupantId(
      anchorSnappedSides.right,
      resolveOccupantItem,
      String(item.id),
    )
    if (usingSelectedAnchor) {
      if (selectedSceneOccupiedSides.left !== undefined) {
        occupiedLeft = selectedSceneOccupiedSides.left
      }
      if (selectedSceneOccupiedSides.right !== undefined) {
        occupiedRight = selectedSceneOccupiedSides.right
      }
      if (shouldForceSelectedLeftPlacement) {
        occupiedLeft = selectedLeftModuleOccupant
        occupiedRight = selectedRightModuleOccupant
      }
    }
    // Módulo que sí admite brazo: el brazo del labio se reancla al nuevo extremo.
    // Pouff: exterior del brazo (el BR se mantiene).
    // RELAX: snap normal al módulo (el BR se purga después). No usar beyond-arm
    // o queda un hueco sin colisión. Corner/oreja no usan este path.
    let effectiveDirection = directionMultiplier
    let placedBeyondArm = false

    if (
      drawingIsArm &&
      hasArmOnUnitSide(anchorItem, snapSide, sceneItems, String(item.id))
    ) {
      const otherSide: 'left' | 'right' = snapSide === 'left' ? 'right' : 'left'
      const otherBlocked =
        Boolean(otherSide === 'left' ? occupiedLeft : occupiedRight) ||
        hasArmOnUnitSide(anchorItem, otherSide, sceneItems, String(item.id))
      if (otherBlocked || enforceCatalogSide || anchorHandedArmSide != null) {
        return {
          status: 'abort',
          reason: 'no-free-side-on-anchor',
          data: {
            itemId: String(item.id),
            anchorId: String((anchorItem as any)?.id ?? ''),
            snapSide,
            occupiedBy: 'arm',
          },
        }
      }
      effectiveDirection = -directionMultiplier
      snapSide = otherSide
    }

    const sideOccupant = snapSide === 'left' ? occupiedLeft : occupiedRight

    if (drawingIsArm) {
      const occupant = resolveSceneItem(store, sceneItems, sideOccupant)
      if (
        isPouffSceneItem(occupant) ||
        pouffAdjacentToAnchorSide(anchorItem, snapSide, sceneItems, String(item.id))
      ) {
        return {
          status: 'abort',
          reason: 'arm-blocked-by-pouff',
          data: {
            itemId: String(item.id),
            anchorId: String((anchorItem as any)?.id ?? ''),
            snapSide,
          },
        }
      }
    }

    // Solo pouff. RELAX no: snapea al módulo y realign borra el BR (evita hueco).
    if (drawingIsPouff) {
      const armOnPreferredSide = getArmOnUnitSide(
        anchorItem,
        snapSide,
        sceneItems,
        String(item.id),
      )
      const otherSide: 'left' | 'right' = snapSide === 'left' ? 'right' : 'left'
      const armOnOtherSide = getArmOnUnitSide(
        anchorItem,
        otherSide,
        sceneItems,
        String(item.id),
      )
      const armForBeyond = armOnPreferredSide || armOnOtherSide
      if (armForBeyond) {
        if (armOnOtherSide && !armOnPreferredSide) {
          snapSide = otherSide
          effectiveDirection = -directionMultiplier
        }
        const beyondArm = computePlacementBeyondArm(armForBeyond, item, store)
        if (beyondArm) {
          centerPosition.copy(beyondArm.position)
          quaternion.copy(beyondArm.quaternion)
          pendingParentSnap = null
          placedBeyondArm = true
          logDrawParentSnap('placement-beyond-arm', {
            itemId: String(item.id),
            armId: String(armForBeyond.id),
            anchorModuleId: String((anchorItem as any)?.id ?? ''),
            drawingIsPouff: true,
            snapSide,
          })
        }
      }
    }

    if (!placedBeyondArm && enforceCatalogSide && sideOccupant && sideOccupant !== String(item.id)) {
      return {
        status: 'abort',
        reason: 'catalog-required-side-occupied',
        data: {
          desiredSnapSide,
          sideOccupant,
          itemId: String(item.id),
          anchorId: String((anchorItem as any)?.id ?? ''),
          occupiedLeft,
          occupiedRight,
          drawingIsOreja,
        },
      }
    }

    if (
      !placedBeyondArm &&
      desiredSnapSide == null &&
      anchorHandedArmSide == null &&
      !shouldForceSelectedLeftPlacement &&
      sideOccupant &&
      sideOccupant !== String(item.id)
    ) {
      const otherSide: 'left' | 'right' = snapSide === 'left' ? 'right' : 'left'
      const otherOccupant = otherSide === 'left' ? occupiedLeft : occupiedRight
      const otherIsPouff = isPouffSceneItem(resolveSceneItem(store, sceneItems, otherOccupant))

      if (drawingIsArm && otherIsPouff) {
        return {
          status: 'abort',
          reason: 'arm-blocked-by-pouff',
          data: {
            itemId: String(item.id),
            anchorId: String((anchorItem as any)?.id ?? ''),
            snapSide: otherSide,
          },
        }
      }

      if (!otherOccupant || otherOccupant === String(item.id)) {
        effectiveDirection = -directionMultiplier
        snapSide = otherSide
        if (
          drawingIsArm &&
          pouffAdjacentToAnchorSide(anchorItem, snapSide, sceneItems, String(item.id))
        ) {
          return {
            status: 'abort',
            reason: 'arm-blocked-by-pouff',
            data: {
              itemId: String(item.id),
              anchorId: String((anchorItem as any)?.id ?? ''),
              snapSide,
            },
          }
        }
        // Tras flip: solo pouff fuera del brazo (RELAX no).
        if (drawingIsPouff) {
          const armAfterFlip = getArmOnUnitSide(
            anchorItem,
            snapSide,
            sceneItems,
            String(item.id),
          )
          if (armAfterFlip) {
            const beyondArm = computePlacementBeyondArm(armAfterFlip, item, store)
            if (beyondArm) {
              centerPosition.copy(beyondArm.position)
              quaternion.copy(beyondArm.quaternion)
              pendingParentSnap = null
              placedBeyondArm = true
              logDrawParentSnap('placement-beyond-arm-after-flip', {
                itemId: String(item.id),
                armId: String(armAfterFlip.id),
                snapSide,
              })
            }
          }
        }
      } else if (drawingIsPouff) {
        const armFallback = getArmOnUnitSide(
          anchorItem,
          otherSide,
          sceneItems,
          String(item.id),
        )
        if (armFallback) {
          const beyondArm = computePlacementBeyondArm(armFallback, item, store)
          if (beyondArm) {
            centerPosition.copy(beyondArm.position)
            quaternion.copy(beyondArm.quaternion)
            pendingParentSnap = null
            placedBeyondArm = true
            snapSide = otherSide
            logDrawParentSnap('placement-beyond-arm-fallback', {
              itemId: String(item.id),
              armId: String(armFallback.id),
            })
          }
        }
        if (!placedBeyondArm) {
          return {
            status: 'abort',
            reason: 'no-free-side-on-anchor',
            data: {
              itemId: String(item.id),
              anchorId: String((anchorItem as any)?.id ?? ''),
              occupiedLeft,
              occupiedRight,
            },
          }
        }
      } else {
        return {
          status: 'abort',
          reason: 'no-free-side-on-anchor',
          data: {
            itemId: String(item.id),
            anchorId: String((anchorItem as any)?.id ?? ''),
            occupiedLeft,
            occupiedRight,
          },
        }
      }
    }

    const anchorCatalog = String(anchorItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    const isAnchorRinconero = isRinconeroCatalog(anchorCatalog)
    const anchorIsOreja = isOreja(anchorCatalog)

    const drawingIsStraightModule =
      isStraightModularSnapCatalog(normalizedDrawingCatalog) && !drawingIsArm && !drawingIsPouff
    const moduleWidth =
      modulePlanWidth(item) ||
      Number(item.width || 0) ||
      defaultWidthForDrawCatalog(normalizedDrawingCatalog)
    const moduleThickness =
      Number(item.thickness || 0) ||
      defaultThicknessForDrawCatalog(normalizedDrawingCatalog)

    const newPosition = new Vector3()
    const newQuaternion = anchorQuat.clone()
    let placementKind: 'rinconero-leg' | 'rinconero-lateral' | 'straight-row' = 'straight-row'

    if (!placedBeyondArm) {
    if (enforceCatalogSide && snapSide !== desiredSnapSide) {
      return {
        status: 'abort',
        reason: 'catalog-snap-side-mismatch',
        data: {
          desiredSnapSide,
          snapSide,
          itemId: String(item.id),
          anchorId: String((anchorItem as any)?.id ?? ''),
          drawingIsOreja,
        },
      }
    }

    // Módulo recto tras rinconero 166-: pata (L) o fila lateral (misma lógica 160/167).
    if (isAnchorRinconero && !anchorIsOreja && drawingIsStraightModule && !drawingIsOreja) {
      const straightSnap = resolveStraightModuleRinconeroSnap({
        rinconero: anchorItem,
        moduleWidth,
        moduleThickness,
        mode: 'draw',
        items: store.items as Map<string, any>,
        rinconeroId: String((anchorItem as any)?.id ?? ''),
        excludeIds: [String(item.id)],
        forcedSnapSide: options?.chainedDrawSnapSide,
      })
      if (straightSnap?.matrix) {
        straightSnap.matrix.decompose(newPosition, newQuaternion, new Vector3())
        placementKind = straightSnap.face === 'leg' ? 'rinconero-leg' : 'rinconero-lateral'
        snapSide = straightSnap.snapSide
        drawSnapLog(straightSnap.face === 'leg' ? 'rinconero-leg' : 'rinconero-lateral', {
          drawingCatalog: normalizedDrawingCatalog,
          anchorCatalog,
          anchorId: String((anchorItem as any)?.id ?? ''),
          moduleWidth,
          anchorWidth: rinconeroPlanWidth(anchorItem),
          snapSide: straightSnap.snapSide,
          face: straightSnap.face,
        })
      }
    } else {
      const widthToUse = effectiveDirection > 0 ? anchorItem.width || 0 : item.width || 0
      const offsetDistance = effectiveDirection * widthToUse
      newPosition.copy(anchorPos).add(anchorXAxis.clone().multiplyScalar(offsetDistance))
      drawSnapLog('straight-row', {
        drawingCatalog: normalizedDrawingCatalog,
        anchorCatalog,
        anchorId: String((anchorItem as any)?.id ?? ''),
        snapSide,
        effectiveDirection,
        widthToUse,
      })
    }

    newPosition.y = item.y
    centerPosition.copy(newPosition)
    quaternion.copy(newQuaternion)
    logDrawParentSnap('computed-parent-position', {
      itemId: String(item.id),
      anchorId: String((anchorItem as any)?.id ?? ''),
      snapSide,
      desiredSnapSide,
      directionMultiplier,
      effectiveDirection,
      placementKind,
      position: {
        x: Number(newPosition.x.toFixed(2)),
        y: Number(newPosition.y.toFixed(2)),
        z: Number(newPosition.z.toFixed(2)),
      },
    })
    drawSnapLog('colocacion-final', {
      drawingCatalog: normalizedDrawingCatalog,
      anchorCatalog,
      placementKind,
      snapSide,
      position: {
        x: Number(newPosition.x.toFixed(2)),
        y: Number(newPosition.y.toFixed(2)),
        z: Number(newPosition.z.toFixed(2)),
      },
    })
    pendingParentSnap = {
      anchorId: anchorItem.id as AnyID,
      snapSide,
    }
    }
  }

  // Puff: validar OBB real antes de confirmar. Sin penetración, sin mover otras piezas.
  if (drawingIsPouff) {
    const pouffCollisionIds = new Set<string>()
    store.collisionableItems.forEach((cid: string) => pouffCollisionIds.add(String(cid)))
    store.items.forEach((sceneItem: any, sceneId: string) => {
      if (!sceneItem?.matrix || sceneItem.visible === false) return
      if (sceneItem.type !== 'linkedItem' && sceneItem.type !== 'items') return
      if (String(sceneId) === String(item.id)) return
      pouffCollisionIds.add(String(sceneId))
    })
    const validated = resolveValidPouffInsertPose(
      centerPosition,
      quaternion,
      item,
      store.items as Map<string, any>,
      pouffCollisionIds as Set<string>,
    )
    if (!validated) {
      return {
        status: 'abort',
        reason: 'pouff-no-valid-exterior-slot',
        data: {
          itemId: String(item.id),
          catalogID: normalizedDrawingCatalog,
        },
      }
    }
    centerPosition.copy(validated.position)
    quaternion.copy(validated.quaternion)
    // No registrar snappedSides: el puff no forma fila ni desplaza brazos/módulos.
    pendingParentSnap = null
    logDrawParentSnap('pouff-insert-validated', {
      itemId: String(item.id),
      position: {
        x: Number(centerPosition.x.toFixed(2)),
        y: Number(centerPosition.y.toFixed(2)),
        z: Number(centerPosition.z.toFixed(2)),
      },
    })
  }

  return { status: 'placed', centerPosition, quaternion, pendingParentSnap }
}
